import React, { useCallback, useMemo, useState } from "react";
import { Autocomplete, GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { createGoogleMapsJsLoaderProps, getMapsApiKeyFromEnv } from "../maps/googleMapsLoader";

/** @param {number | null} latitude @param {number | null} longitude @param {string} [venueLabel] */
function buildGoogleMapsOpenUrl(latitude, longitude, venueLabel) {
  const hasCoords =
    latitude != null &&
    longitude != null &&
    !Number.isNaN(latitude) &&
    !Number.isNaN(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180;
  if (hasCoords) {
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
  }
  const q = (venueLabel || "").trim();
  if (q) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
  }
  return null;
}

function OpenInGoogleMapsRow({ latitude, longitude, venueLabel }) {
  const href = useMemo(
    () => buildGoogleMapsOpenUrl(latitude, longitude, venueLabel),
    [latitude, longitude, venueLabel]
  );
  if (!href) return null;
  return (
    <div className="map-picker__open-row">
      <a
        className="ghost-btn small map-picker__open-maps"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open in Google Maps
      </a>
    </div>
  );
}

const mapContainerStyle = {
  width: "100%",
  height: "280px",
  borderRadius: "12px"
};

const DEFAULT_CENTER = { lat: 19.076, lng: 72.8777 };

function ManualFallback({ latitude, longitude, venueLabel, onLocationChange }) {
  return (
    <div className="map-picker map-picker--fallback">
      <p className="muted">
        <strong>No Maps API key loaded.</strong> Put your key in <code>.env</code> as{" "}
        <code>REACT_APP_GOOGLE_MAPS_API_KEY=your_key</code>, then <strong>save the file</strong> (Ctrl+S) and{" "}
        <strong>restart</strong> <code>npm start</code>. Enable Maps JavaScript API and Places API in Google Cloud.
      </p>
      <p className="muted" style={{ fontSize: "0.85rem" }}>
        If the key is only in the editor but not saved, React still sees an empty key and shows these fields instead
        of the map.
      </p>
      <div className="map-picker__manual">
        <label>
          Latitude
          <input
            type="number"
            step="any"
            value={latitude ?? ""}
            placeholder="e.g. 19.076"
            onChange={(e) => {
              const raw = e.target.value.trim();
              const v = raw === "" ? null : Number.parseFloat(raw);
              onLocationChange({
                lat: v !== null && !Number.isNaN(v) ? v : null,
                lng: longitude ?? undefined,
                address: "",
                placeId: ""
              });
            }}
          />
        </label>
        <label>
          Longitude
          <input
            type="number"
            step="any"
            value={longitude ?? ""}
            placeholder="e.g. 72.8777"
            onChange={(e) => {
              const raw = e.target.value.trim();
              const v = raw === "" ? null : Number.parseFloat(raw);
              onLocationChange({
                lat: latitude ?? undefined,
                lng: v !== null && !Number.isNaN(v) ? v : null,
                address: "",
                placeId: ""
              });
            }}
          />
        </label>
      </div>
      <OpenInGoogleMapsRow latitude={latitude} longitude={longitude} venueLabel={venueLabel} />
    </div>
  );
}

function LoaderErrorHelp() {
  return (
    <div className="map-picker-troubleshoot muted">
      <p>
        Typical fixes (Google Cloud Console, same project as your API key): enable{" "}
        <strong>Billing</strong>; enable APIs <strong>Maps JavaScript API</strong> and{" "}
        <strong>Places API</strong>; under Credentials → key → website restrictions add{" "}
        <code>http://localhost:3000/*</code>.
      </p>
      <p>
        <a
          href="https://developers.google.com/maps/documentation/javascript/error-messages"
          target="_blank"
          rel="noopener noreferrer"
        >
          Maps JavaScript API error messages
        </a>
      </p>
    </div>
  );
}

/** Loads Maps + Places via one shared loader config ({@link ../maps/googleMapsLoader}). */
function LocationMapWithGoogle({ apiKey, latitude, longitude, venueLabel, onLocationChange }) {
  const [autocomplete, setAutocomplete] = useState(null);

  const loaderOpts = useMemo(() => createGoogleMapsJsLoaderProps(apiKey), [apiKey]);

  const { isLoaded, loadError } = useJsApiLoader(loaderOpts);

  const markerPosition = useMemo(() => {
    if (latitude != null && longitude != null && !Number.isNaN(latitude) && !Number.isNaN(longitude)) {
      return { lat: latitude, lng: longitude };
    }
    return null;
  }, [latitude, longitude]);

  const mapCenter = markerPosition || DEFAULT_CENTER;

  const applyPoint = useCallback(
    (lat, lng, address, placeId) => {
      onLocationChange({
        lat,
        lng,
        address,
        placeId: placeId ?? ""
      });
    },
    [onLocationChange]
  );

  const onMapClick = useCallback(
    (e) => {
      if (!e.latLng) return;
      applyPoint(e.latLng.lat(), e.latLng.lng(), undefined, "");
    },
    [applyPoint]
  );

  const onMarkerDragEnd = useCallback(
    (e) => {
      if (!e.latLng) return;
      applyPoint(e.latLng.lat(), e.latLng.lng(), undefined, "");
    },
    [applyPoint]
  );

  const onPlaceChanged = useCallback(() => {
    if (!autocomplete) return;
    const place = autocomplete.getPlace();
    const loc = place.geometry?.location;
    if (!loc) return;
    const lat = loc.lat();
    const lng = loc.lng();
    const address = place.formatted_address || place.name || "";
    const placeId = place.place_id || "";
    applyPoint(lat, lng, address, placeId);
  }, [autocomplete, applyPoint]);

  if (loadError) {
    const msg =
      typeof loadError === "object" && loadError !== null && "message" in loadError
        ? String(loadError.message)
        : String(loadError);
    return (
      <div className="map-picker map-picker--error">
        <p className="error-text">Google Maps script failed to load.</p>
        <p className="muted map-picker__error-detail">{msg}</p>
        <LoaderErrorHelp />
      </div>
    );
  }

  if (!isLoaded) {
    return <p className="muted">Loading map...</p>;
  }

  return (
    <div className="map-picker">
      <Autocomplete onLoad={setAutocomplete} onPlaceChanged={onPlaceChanged}>
        <input
          className="map-picker__search"
          type="text"
          placeholder="Search for a venue or address"
          aria-label="Search venue address"
        />
      </Autocomplete>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={mapCenter}
        zoom={markerPosition ? 15 : 11}
        onClick={onMapClick}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false
        }}
      >
        {markerPosition ? (
          <Marker position={markerPosition} draggable onDragEnd={onMarkerDragEnd} />
        ) : null}
      </GoogleMap>
      <p className="muted map-picker__hint">Click the map or drag the pin to set the exact venue.</p>
      <OpenInGoogleMapsRow latitude={latitude} longitude={longitude} venueLabel={venueLabel} />
    </div>
  );
}

/**
 * @param {{
 *   latitude: number | null,
 *   longitude: number | null,
 *   venueLabel?: string,
 *   onLocationChange: (next: { lat?: number | null; lng?: number | null; address?: string; placeId?: string }) => void,
 * }} props
 */
function LocationMapPicker(props) {
  const apiKey = getMapsApiKeyFromEnv();
  const venueLabel = props.venueLabel ?? "";

  if (!apiKey) {
    return (
      <ManualFallback
        latitude={props.latitude}
        longitude={props.longitude}
        venueLabel={venueLabel}
        onLocationChange={props.onLocationChange}
      />
    );
  }

  return <LocationMapWithGoogle apiKey={apiKey} {...props} venueLabel={venueLabel} />;
}

export default LocationMapPicker;
